/**
 * Alpine's isClickEvent(eventName) calls eventName.includes(...) without null checks.
 * During Livewire wire:navigate re-init, some browsers (notably Mobile Safari) surface
 * events whose `type` is null, which throws TypeError in the navigate plugin.
 * See TMAEX-4332.
 */

const PATCHED_FLAG = '__allianceSafeEventTypeGuardPatched';

/** @type {WeakMap<EventListenerOrObject, EventListener>} */
const wrappedListeners = new WeakMap();

/**
 * @param {string | null | undefined} type
 * @returns {string}
 */
export function normalizeEventType(type) {
    return type == null ? '' : type;
}

/**
 * @param {Event | null | undefined} event
 * @returns {Event | null | undefined}
 */
export function fixEventInstance(event) {
    if (event != null && event.type == null) {
        try {
            Object.defineProperty(event, 'type', {
                value: '',
                configurable: true,
                enumerable: true,
            });
        } catch {
            // Some platform Event objects may not allow redefining type.
        }
    }

    return event;
}

/**
 * @param {EventListenerOrObject | null} listener
 * @returns {EventListenerOrObject | null}
 */
export function wrapListenerForSafeEventType(listener) {
    if (listener == null) {
        return listener;
    }

    if (wrappedListeners.has(listener)) {
        return wrappedListeners.get(listener);
    }

    let wrapped;

    if (typeof listener === 'function') {
        wrapped = function (event) {
            fixEventInstance(event);

            return listener.call(this, event);
        };
    } else if (typeof listener.handleEvent === 'function') {
        wrapped = function (event) {
            fixEventInstance(event);

            return listener.handleEvent(event);
        };
    } else {
        return listener;
    }

    wrappedListeners.set(listener, wrapped);

    return wrapped;
}

/**
 * @param {EventListenerOrObject | null} listener
 * @returns {EventListenerOrObject | null}
 */
export function getWrappedListener(listener) {
    if (listener == null) {
        return listener;
    }

    return wrappedListeners.get(listener) ?? listener;
}

/**
 * Install guards so Event#type is never null/undefined when read by Alpine/Livewire.
 */
export function installSafeEventTypeGuard() {
    if (typeof globalThis.Event === 'undefined') {
        return;
    }

    patchEventTargetListeners();
}

function patchEventTargetListeners() {
    if (typeof EventTarget === 'undefined' || EventTarget.prototype[PATCHED_FLAG]) {
        return;
    }

    const nativeAddEventListener = EventTarget.prototype.addEventListener;
    const nativeRemoveEventListener = EventTarget.prototype.removeEventListener;

    EventTarget.prototype.addEventListener = function addEventListener(type, listener, options) {
        const wrappedListener = wrapListenerForSafeEventType(listener);

        return nativeAddEventListener.call(this, type, wrappedListener, options);
    };

    EventTarget.prototype.removeEventListener = function removeEventListener(type, listener, options) {
        const wrappedListener = getWrappedListener(listener);

        return nativeRemoveEventListener.call(this, type, wrappedListener, options);
    };

    Object.defineProperty(EventTarget.prototype, PATCHED_FLAG, { value: true });
}
